import React, { useEffect, useRef, useState } from 'react';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import { ReminderListItemDto } from 'api/reminders/models/ReminderListItemDto';
import { RemindersSearchCriteria } from 'api/reminders/models/RemindersSearchCriteria';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { Reducers } from 'store/types';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import ReminderFiltersScreen, { Filters } from 'screens/reminders/components/ReminderFiltersScreen';
import ListRemindersScreen from 'screens/reminders/ListRemindersScreen';
import ReminderScreen from './components/ReminderScreen';
import styles from './CarReminderTab.module.scss';

enum ScreenType {
    LIST = 'LIST',
    REMINDER = 'REMINDER'
}

type Props = {
    vehicle?: VehicleDto;
    onDeleteFromList?: () => void;
    tourReRender: () => void;
}

const CarReminderTab = ({ onDeleteFromList, vehicle, tourReRender }: Props) => {
    const { vehicleId, tabId, itemType } = useParams<{ vehicleId: string, tabId: string, itemType: string, itemId: string }>();
    const history = useHistory();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRemindersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_WRITE']);

    const editable = !(vehicle && !vehicle.active);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const FILTERS_CACHE_KEY = 'REMINDERSFILTER';
    const isFirstLoad = useRef<boolean>(true);

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const [criteria, setCriteria] = useState<RemindersSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'r.created_date',
        orderBy: 'desc',
        vehicleId: vehicleId,
        vehiclesInactive: true,
    });

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.categoryId && f.categoryId.length > 0) total++;
        setFiltersTotal(total);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            onChangeFilters(JSON.parse(cachedFilters), true);
        } else {
            onChangeFilters({}, true);
        }
    };

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        setFilters({ ..._filters });
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        setCriteria(prev => ({
            ...prev,
            vehicleId: vehicleId,
            categoryId: _filters.categoryId,
            orderBy: _filters.orderBy || prev.orderBy || 'desc',
            orderColumn: _filters.orderColumn || prev.orderColumn || 'r.created_date',
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
        }));

        isFirstLoad.current = false;
    };

    useEffect(() => {
        loadFilters();
    }, []);

    const onRowClick = (item: ReminderListItemDto, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (item.vehicleMaintenanceId && item.vehicleId) {
            history.push(`/vehicles/details/${item.vehicleId}/maintenance/details/${item.vehicleMaintenanceId}`);
            return;
        }
        if (item.useContractId && item.vehicleId) {
            history.push(`/vehicles/details/${item.vehicleId}/contract/details/use/details/${item.useContractId}`);
            return;
        }
        if (item.maintenanceContractId && item.vehicleId) {
            history.push(`/vehicles/details/${item.vehicleId}/contract/details/maintenance/details/${item.maintenanceContractId}`);
            return;
        }
        if (item.insuranceContractId && item.vehicleId) {
            history.push(`/vehicles/details/${item.vehicleId}/contract/details/insurance/details/${item.insuranceContractId}`);
            return;
        }

        history.push(`/vehicles/details/${vehicleId}/${tabId}/details/${item.id}`);
    };

    const isList = (itemType ?? 'list') === ScreenType.LIST.toString().toLowerCase();

    return (
        <div id="five-step">
            {isList &&
                <div className={styles.listWrapper}>
                    <div className={styles.tabHeader}>
                        <div></div>
                        <div className={styles.rowFlex}>
                            <Popover
                                contentContainerClassName={styles.filtersPopoverContainer}
                                containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                positions={['left']}
                                onClickOutside={() => updateTotalFilters(filters)}
                                content={setIsPopoverOpen => <ReminderFiltersScreen
                                    isVehicle={true}
                                    filters={filters}
                                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                                    onChange={f => updateTotalFilters(f)}
                                />}
                            >
                                {(isPopoverOpen, setIsPopoverOpen) => (
                                    <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                        {filtersTotal > 0 &&
                                            <div className={styles.counterList}><div className={styles.counterNumber}>{filtersTotal}</div></div>
                                        }
                                    </ScreenHeaderButton>
                                )}
                            </Popover>
                            <ExportListingButton exportRef={exportListingRef} />
                            {editable && hasRemindersWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/vehicles/details/${vehicleId}/${tabId}/new`)} />}
                        </div>
                    </div>
                    {!isFirstLoad.current &&
                        <ListRemindersScreen
                            editable={editable}
                            criteria={criteria}
                            onClickItem={onRowClick}
                            exportRef={exportListingRef}
                        />
                    }
                </div>
            }

            {!isList &&
                <ReminderScreen
                    isVehicle
                    onBackList={() => history.goBack()}
                />
            }
        </div>
    );
}

export default CarReminderTab;
